import { h, toast } from './dom.js';
import type { DesktopTidyPlan, DesktopBridge } from '../../session/controller.js';

export interface DesktopTidyController {
  open(): Promise<void>;
  createButton(): HTMLButtonElement;
  destroy(): void;
}

export function createDesktopTidyController(options: {
  bridge: NonNullable<DesktopBridge['desktop']>;
}): DesktopTidyController {
  let active = true;
  let overlay: HTMLElement | null = null;
  let opening: Promise<void> | null = null;
  let returnFocus: HTMLElement | null = null;

  function close(): void {
    overlay?.remove(); overlay = null;
    if (returnFocus?.isConnected) returnFocus.focus();
    returnFocus = null;
  }

  function installDialog(title: string): { card: HTMLElement; titleEl: HTMLElement } {
    close();
    returnFocus = document.activeElement as HTMLElement | null;
    const titleId = `desktop-tidy-title-${Date.now()}`;
    const titleEl = h('h2', { class: 'modal-title', id: titleId }, [title]);
    const card = h('div', { class: 'modal-card desktop-tidy-dialog' }, [titleEl]);
    overlay = h('div', { class: 'modal-overlay', role: 'dialog', 'aria-modal': 'true', 'aria-labelledby': titleId }, [card]);
    overlay.addEventListener('keydown', event => {
      if (event.key === 'Escape') { event.preventDefault(); close(); return; }
      if (event.key !== 'Tab' || !overlay) return;
      const controls = [...overlay.querySelectorAll<HTMLElement>('button:not([disabled]),a[href]')];
      const first = controls[0]; const last = controls.at(-1);
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    });
    document.body.appendChild(overlay);
    return { card, titleEl };
  }

  function showPlan(plan: DesktopTidyPlan): void {
    const { card } = installDialog('Ordenar escritorio');
    const summary = h('p', { class: 'modal-body' }, [plan.total === 1 ? 'ASIS ha encontrado 1 archivo suelto.' : `ASIS ha encontrado ${plan.total} archivos sueltos.`]);
    if (plan.omittedCount) summary.append(` Se ${plan.omittedCount === 1 ? 'ha omitido 1 nombre no seguro' : `han omitido ${plan.omittedCount} nombres no seguros`} sin tocarlo${plan.omittedCount === 1 ? '' : 's'}.`);
    const counts = h('ul', { class: 'desktop-tidy-counts', 'aria-label': 'Recuento por tipo' });
    for (const [type, count] of Object.entries(plan.counts)) {
      if (count > 0) counts.append(h('li', {}, [h('span', {}, [type]), h('strong', {}, [String(count)])]));
    }
    const files = h('ul', { class: 'desktop-tidy-files', 'aria-label': 'Archivos que se moverán' });
    for (const file of plan.files) files.append(h('li', {}, [h('span', {}, [file.name]), h('span', { class: 'badge' }, [file.type])]));
    if (plan.total === 0) files.append(h('li', { class: 'empty' }, ['No hay archivos que ordenar.']));
    const status = h('p', { class: 'desktop-tidy-status', role: 'status', 'aria-live': 'polite' });
    const cancel = h('button', { class: 'btn ghost', type: 'button' }, ['Cerrar']);
    const apply = h('button', { class: 'btn primary', type: 'button', disabled: plan.total === 0 }, ['Ordenar archivos']);
    const actions = h('div', { class: 'modal-actions' }, [cancel, apply]);
    cancel.addEventListener('click', close);
    function addUndo(manifestId: string, label = 'Deshacer'): void {
      const undo = h('button', { class: 'btn ghost', type: 'button' }, [label]);
      undo.addEventListener('click', async () => {
        undo.disabled = cancel.disabled = apply.disabled = true;
        status.textContent = 'Deshaciendo…';
        try {
          const undone = await options.bridge.undo(manifestId);
          status.textContent = undone.ok
            ? `Se han devuelto ${undone.restoredCount} archivos al escritorio.`
            : `Se han devuelto ${undone.restoredCount}; ${undone.failedCount} no se han podido devolver porque cambiaron.`;
          cancel.disabled = false;
          apply.disabled = plan.total === 0;
          if (undone.ok) { undo.remove(); cancel.focus(); } else { undo.disabled = false; undo.focus(); }
        } catch {
          status.textContent = 'No se ha podido deshacer. Los archivos movidos permanecen en Ordenado por ASIS.';
          undo.disabled = false;
          cancel.disabled = false;
          apply.disabled = plan.total === 0;
          undo.focus();
        }
      });
      actions.insertBefore(undo, cancel);
    }
    if (plan.recoveryManifestId) addUndo(plan.recoveryManifestId, 'Deshacer ordenación anterior');
    apply.addEventListener('click', async () => {
      apply.disabled = cancel.disabled = true;
      status.textContent = 'Ordenando…';
      try {
        const result = await options.bridge.apply(plan.id);
        status.textContent = result.ok
          ? `Se han movido ${result.movedCount} de ${result.plannedCount} archivos.`
          : `Se han movido ${result.movedCount} de ${result.plannedCount}. El resto no se ha tocado.`;
        apply.remove();
        cancel.disabled = false;
        if (result.manifestId) addUndo(result.manifestId);
        cancel.focus();
      } catch {
        status.textContent = 'El plan ha caducado o algún archivo ha cambiado. Vuelve a preparar el plan.';
        cancel.disabled = false;
        cancel.focus();
      }
    });
    card.append(summary, counts, files, status, actions);
    cancel.focus();
  }

  async function open(): Promise<void> {
    if (!active) return;
    if (overlay) { overlay.querySelector<HTMLElement>('button')?.focus(); return; }
    if (opening) return opening;
    const { card } = installDialog('Preparando el plan');
    const closeButton = h('button', { class: 'btn ghost', type: 'button' }, ['Cerrar']);
    closeButton.addEventListener('click', close);
    card.append(h('p', { class: 'modal-body', role: 'status' }, ['Revisando únicamente los archivos sueltos del escritorio…']), h('div', { class: 'modal-actions' }, [closeButton]));
    closeButton.focus();
    opening = options.bridge.plan().then(async plan => {
      if (!active) return;
      showPlan(plan);
    }).catch(error => {
      if (active) { close(); toast(/EACCES|EPERM|permission/iu.test(error instanceof Error ? error.message : '') ? 'macOS no permite acceder al Escritorio. Ve a Ajustes del Sistema → Privacidad y seguridad → Archivos y carpetas y activa Escritorio para ASIS.' : 'No se ha podido preparar el plan del escritorio.', 'error'); }
    }).finally(() => { opening = null; });
    return opening;
  }

  return {
    open,
    createButton() {
      const button = h('button', { class: 'btn ghost', type: 'button' }, ['Preparar plan']);
      button.addEventListener('click', () => { void open(); });
      return button;
    },
    destroy() { active = false; close(); },
  };
}
